import Layout from "@/components/elements/Layout";
import SubNavigation from "@/components/elements/SubNavigation";
import ShareButtons from "@/components/parts/ShareButtons";
import Schema from "@/components/schema/Schema";
import { Button } from "@/components/ui/button";
import { useGetCurrentUrl } from "@/hooks";
import useAuth from "@/hooks/auth";
import {
  getContentDetailBySlug,
  getContentsByContentTypeName,
  trackContentAccessCounting,
  getRelatedContentName,
} from "@/services/api";
import {
  CONTENT_TYPES,
  cleanHTML,
  getVideoPlaybackUrl,
} from "@/utils/content";
import { formatDate } from "@/utils/datetime";
import { ArrowLeft, CaretRight, Lock, LockKey } from "@phosphor-icons/react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState, useRef } from "react";
import ReactPlayer from "react-player";
import { fitIn } from "@/utils/imageHandler";
import { useAtom } from "jotai";
import { purchasePopup, loginPopup, userAtom } from "@/Atoms";
import mux from "mux-embed";
import RelatedContentSidebar from "@/components/RelatedContentSidebar";
import ContentWishlistComponent from "@/components/parts/ContentWishlistComponent";
import MediaVideo from "@/components/MediaVideo";
import RelatedReport from "@/components/RelatedReport";
import { useIsMobile } from "@/hooks/useIsMobile";
import CarouselVideo from "@/components/parts/CarouselVideo";

function VideoDetail({ video, otherVideos }) {
  const [reach_limit, setReachLimit] = useState(false);
  const { user } = useAuth();
  const [userState, setUserState] = useAtom(userAtom);
  const isMobile = useIsMobile();
  const router = useRouter();
  const currentPath = router.asPath;
  const fullUrl = useGetCurrentUrl();
  const [openPurchasePopup, setOpenPurchasePopup] = useAtom(purchasePopup);
  const [openLoginPopup, setOpenLoginPopup] = useAtom(loginPopup);

  const fetchMeteredContent = async () => {
    if (userState?.roles === "admin-team" || userState?.roles === "read-access" || video.is_free) {
      setReachLimit(false);
    } else {
      try {
        const response = await trackContentAccessCounting(router.query.slug, {
          ip_address: localStorage.getItem("lctd-ia"),
          user_id: userState?.id,
          user_email: userState?.email,
        });
        setReachLimit(response.data.reach_limit);
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    fetchMeteredContent();
  }, [router.query.slug, userState]);

  const goToLogin = () => {
    setOpenLoginPopup(true);
    // router.push(`/auth/login?redirect_to=${currentPath}`);
  };

  function toPricing() {
    setOpenPurchasePopup("content");
    // router.push(`/pricing?redirect_to=${currentPath}`);
  }

  const structuredData = [
    {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${process.env.BASE_URL}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Videos",
          item: `${process.env.BASE_URL}videos`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: video.title,
          item: `${process.env.BASE_URL}videos/${router.query.slug}`,
        },
      ],
    },
  ];

  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      const videoElement = videoRef.current.getInternalPlayer();

      // Mux can only watch a <video> element, not the YouTube player.
      if (videoElement instanceof HTMLMediaElement) {
        const initTime = mux.utils.now();
        mux.monitor(videoElement, {
          debug: false,
          data: {
            env_key: process.env.NEXT_PUBLIC_MUX_KEY, // required
            // Metadata fields
            viewer_user_id: userState?.id, // ex: '12345'
            viewer_user_email: userState?.email, // ex: '12345'
            player_name: "Videos", // any arbitrary string you want to use to identify this player
            player_init_time: initTime,
            video_id: video.id, // ex: 'abcd123'
            video_title: video.title, // ex: 'My Great Video'
          },
        });
      }
    }
  }, [videoRef, video]);

  return (
    <>
      <Schema data={structuredData} />
      <Layout
        title={video.title + " - Video"}
        meta_title={video.meta_title}
        description={video.meta_desc}
        meta_image={video.thumbnail_url}
      >
        <SubNavigation title={video.title}></SubNavigation>
        <div className='container relative z-10 items-start lg:flex lg:space-x-8'>
          <div className='flex-1 pb-10 lg:pt-10 lg:pb-16 lg:pr-8 lg:border-r border-white/5'>
            <div className='sticky z-10 -mx-4 overflow-hidden lg:mx-0 top-28 lg:top-0 lg:relative aspect-video lg:rounded-md'>
              {(!user || !reach_limit) && (
                <ReactPlayer
                  ref={videoRef}
                  width={"100%"}
                  height={"100%"}
                  url={getVideoPlaybackUrl(video)}
                  playing={!reach_limit || video.is_free}
                  controls={!reach_limit || video.is_free}
                  config={{
                    file: { attributes: { controlsList: "nodownload" } },
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                  stopOnUnmount={false}
                  pip
                />
              )}

              {/* {!user && !video.is_free && ( */}
              {!user && !video.is_free && reach_limit && (
                <>
                  <Image
                    src={fitIn(video.thumbnail_url, 1000)}
                    layout='fill'
                    objectFit='cover'
                    alt=''
                    className='absolute inset-0 object-cover w-full h-full'
                  />
                  <div className='absolute inset-0 flex flex-col items-center justify-center p-4 bg-background/80 backdrop-blur-md'>
                    <p className='mb-3 text-base font-semibold leading-tight text-center lg:mb-4 lg:text-2xl'>
                      Register for Free or Login to Continue Watching Videos
                    </p>
                    <Button onClick={goToLogin}>
                      <LockKey className='mr-2 text-lg' />
                      <span className=''>Login</span>
                    </Button>
                  </div>
                </>
              )}
            </div>
            <div className='mt-2 lg:mt-4'>
              <h1 className='text-xl font-semibold lg:text-3xl font-display'>
                {video.title}
              </h1>
              <div className=''>
                <div className='flex items-center gap-2 mt-1'>
                  <p className='text-sm text-white/70'>
                    {video.authors.map((author, index) => (
                      <Link
                        key={index}
                        href={`/author/${author.slug}`}
                        className='no-underline'
                      >
                        <span className='underline'>{author.name}</span>
                        {video.authors.length > 2 &&
                          index < video.authors.length - 2 &&
                          ", "}
                        {video.authors.length > 1 &&
                          index === video.authors.length - 2 &&
                          " and "}
                      </Link>
                    ))}
                  </p>
                </div>
                <div className='flex items-center gap-2 mt-2'>
                  <span className='text-sm'>
                    {formatDate(video.publish_date, "MMMM DD, YYYY")}
                  </span>
                  <span className='opacity-40'>&bull;</span>
                  <ContentWishlistComponent triggerSize={"auto"} item={video} />
                </div>

                <div className='flex items-center pt-4 pb-6'>
                  <ShareButtons
                    title={video.title}
                    description={video.meta_desc}
                    url={fullUrl}
                  />
                </div>
              </div>
              <div className='text-white prose-p:mb-6 max-w-none'>
                {cleanHTML(video.description)}
              </div>
            </div>
          </div>
          <div className='lg:pt-10 lg:max-w-[340px] text-white lg:pb-16 space-y-8 lg:space-y-10'>
            <RelatedReport connectedWith={video.connected_with} />

            <div className='space-y-7'>
              {isMobile ? (
                <CarouselVideo
                  title='Other Videos'
                  data={otherVideos.filter(
                    (content) => content.uuid !== video.uuid
                  )}
                  videoHeight={"h-[170px]"}
                  container={false}
                />
              ) : (
                <>
                  {otherVideos.filter((content) => content.uuid !== video.uuid)
                    .length > 0 && (
                    <p className='pb-1 mb-4 text-xl font-semibold border-b lg:text-lg'>
                      Other Videos
                    </p>
                  )}
                  {otherVideos
                    .filter((content) => content.uuid !== video.uuid)
                    .map((item, index) => (
                      <div
                        className='flex items-start space-x-5'
                        key={`video-${index}`}
                      >
                        <div className='relative h-16 aspect-video'>
                          <Link href={`/videos/${item.slug}`}>
                            <Image
                              src={fitIn(item.thumbnail_url, 400)}
                              layout='fill'
                              objectFit='cover'
                              alt=''
                              className='absolute inset-0 object-cover w-full h-full'
                            />
                          </Link>
                        </div>
                        <div className='flex-1'>
                          <Link
                            legacyBehavior
                            href={`/videos/${item.slug}`}
                            passHref
                          >
                            <a className='inline-block -mt-1 text-sm font-semibold leading-snug font-heading'>
                              {item.title}
                            </a>
                          </Link>
                          <p className='mt-2 text-xs leading-snug opacity-50 line-clamp-3'>
                            {item.meta_desc}
                          </p>
                        </div>
                      </div>
                    ))}
                </>
              )}

              <RelatedContentSidebar
                title='Related Podcasts'
                currentContentId={video.uuid}
                contentType={CONTENT_TYPES.PODCAST_EPISODES}
              />
              <RelatedContentSidebar
                title='Did you check these out?'
                currentContentId={video.uuid}
              />
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}

export async function getServerSideProps({ query }) {
  try {
    const response = await getContentDetailBySlug(query.slug, {
      params: {
        contentTypeName: CONTENT_TYPES.VIDEOS,
      },
    });
    const video = response.data.data;
    const videoData = await getRelatedContentName(
      CONTENT_TYPES.VIDEOS,
      video.uuid
    );
    return {
      props: {
        video: response.data.data,
        otherVideos: videoData.data.data || [],
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
}

export default VideoDetail;
